import React, { useState } from 'react';
import {
  XMarkIcon,
  PlayCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

import GlassModal from './Common/GlassModal';
import { fmtDate, fmtTime, companyDateKey, companyTimeKey, companyWallClockToUtc } from '../utils/dateFormatters';

// Humanize a minute count (e.g. 35110 → "24 days 9 hours") instead of showing raw minutes. O-ORION-0731-0047
const formatMinutes = (mins) => {
  const total = Math.round(Math.abs(mins));
  const d = Math.floor(total / 1440);
  const h = Math.floor((total % 1440) / 60);
  const m = total % 60;
  const parts = [];
  if (d) parts.push(`${d} day${d !== 1 ? 's' : ''}`);
  if (h) parts.push(`${h} hour${h !== 1 ? 's' : ''}`);
  if (m && !d) parts.push(`${m} minute${m !== 1 ? 's' : ''}`);
  return parts.join(' ') || '0 minutes';
};

/**
 * StartJobModal
 *
 * Purpose: Confirm job start and record actual start time
 * Industry Standard: ServiceTitan auto-starts, Jobber has manual start, Housecall Pro basic
 *
 * What it does:
 * 1. Records actual start time (defaults to now)
 * 2. Confirms technician on site
 * 3. Optional: Start timer for time tracking
 * 4. Optional: Take "before" photos (placeholder)
 * 5. Changes status from 'scheduled' → 'in_progress'
 * 6. Records actual_start timestamp
 *
 * Part of: Phase 2D - High Priority
 * Competitive Advantage: Match ServiceTitan
 *
 * TODO Phase 5:
 * - Photo upload integration
 * - Timer integration
 * - GPS location verification
 */

const StartJobModal = ({
  isOpen,
  onClose,
  onConfirm,
  jobTitle,
  scheduledStart,
  technicianName
}) => {
  // The date and time fields are COMPANY-local wall clock (the job page renders the schedule
  // through fmtTime, which is company time), so both the default and the write use the
  // company calendar. The old default mixed a UTC day with a browser-local time: in the
  // evening Pacific it offered tomorrow's date at tonight's time.
  const now = new Date();
  const [formData, setFormData] = useState({
    actualStartDate: companyDateKey(now),
    actualStartTime: companyTimeKey(now),
    startTimer: true,
    notes: ''
  });

  const [errors, setErrors] = useState({});

  if (!isOpen) return null;

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.actualStartDate) {
      newErrors.actualStartDate = 'Start date is required';
    }

    if (!formData.actualStartTime) {
      newErrors.actualStartTime = 'Start time is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConfirm = () => {
    if (!validate()) return;

    const actualStartDateTime = companyWallClockToUtc(formData.actualStartDate, formData.actualStartTime);

    onConfirm({
      actualStartDate: formData.actualStartDate,
      actualStartTime: formData.actualStartTime,
      actualStartDateTime,
      startTimer: formData.startTimer,
      notes: formData.notes.trim()
    });

    // Reset form
    const newNow = new Date();
    setFormData({
      actualStartDate: companyDateKey(newNow),
      actualStartTime: companyTimeKey(newNow),
      startTimer: true,
      notes: ''
    });
    setErrors({});
  };

  const getActualStartDateTime = () => {
    if (!formData.actualStartDate || !formData.actualStartTime) return null;
    const iso = companyWallClockToUtc(formData.actualStartDate, formData.actualStartTime);
    return iso ? new Date(iso) : null;
  };

  const actualStartDateTime = getActualStartDateTime();
  const scheduledStartDate = scheduledStart ? new Date(scheduledStart) : null;

  // Check if starting early or late
  let timingStatus = null;
  if (actualStartDateTime && scheduledStartDate) {
    const diffMinutes = (actualStartDateTime - scheduledStartDate) / 1000 / 60;
    if (diffMinutes < -15) {
      timingStatus = { type: 'early', minutes: Math.abs(diffMinutes) };
    } else if (diffMinutes > 15) {
      timingStatus = { type: 'late', minutes: diffMinutes };
    } else {
      timingStatus = { type: 'ontime' };
    }
  }

  return (
    <GlassModal
      isOpen={isOpen}
      onClose={onClose}
      maxWidth="max-w-2xl"
      closeOnOverlay={false}
      header={(
        <>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <PlayCircleIcon className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">Start Job</h2>
              <p className="text-sm text-gray-400 mt-0.5">{jobTitle}</p>
            </div>
          </div>
          <button type="button" aria-label="Close" title="Close" onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </>
      )}
      footer={(
        <>
          <button onClick={onClose} className="px-4 py-2 text-gray-300 hover:text-white font-medium transition-colors">
            Cancel
          </button>
          <button onClick={handleConfirm} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors flex items-center gap-2">
            <PlayCircleIcon className="w-5 h-5" />
            Start Job
          </button>
        </>
      )}
    >



          {/* Quick Start Now Button */}
          <button
            onClick={() => {
              const rightNow = new Date();
              onConfirm({
                actualStartDate: companyDateKey(rightNow),
                actualStartTime: companyTimeKey(rightNow),
                actualStartDateTime: rightNow.toISOString(),
                startTimer: true,
                notes: ''
              });
              setFormData({
                actualStartDate: companyDateKey(rightNow),
                actualStartTime: companyTimeKey(rightNow),
                startTimer: true,
                notes: ''
              });
              setErrors({});
            }}
            className="w-full mb-6 px-6 py-4 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 font-semibold transition-colors flex items-center justify-center gap-3 text-lg"
          >
            <PlayCircleIcon className="w-7 h-7" />
            Start Now
          </button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-3 bg-slate-800 text-gray-400">or set a different start time</span>
            </div>
          </div>

          {/* Scheduled info */}
          {scheduledStartDate && (
            <div className="mb-6 rounded-xl p-4 bg-white/5 border border-white/10 backdrop-blur-sm">
              <p className="text-xs text-gray-400 font-medium mb-1">Scheduled Start</p>
              <p className="text-sm text-white">
                {fmtDate(scheduledStartDate, {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric'
                })} at {fmtTime(scheduledStartDate, {
                  hour: 'numeric',
                  minute: '2-digit'
                })}
              </p>
            </div>
          )}

          {/* Timing Status Warning */}
          {timingStatus && timingStatus.type !== 'ontime' && (
            <div className={`mb-6 border rounded-lg p-4 ${
              timingStatus.type === 'early'
                ? 'bg-yellow-900/30 border-yellow-700'
                : 'bg-orange-900/30 border-orange-700'
            }`}>
              <div className="flex items-start gap-3">
                <ExclamationTriangleIcon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                  timingStatus.type === 'early' ? 'text-yellow-600' : 'text-orange-600'
                }`} />
                <div>
                  <p className={`text-sm font-medium ${
                    timingStatus.type === 'early' ? 'text-yellow-200' : 'text-orange-200'
                  }`}>
                    {timingStatus.type === 'early'
                      ? `Starting ${formatMinutes(timingStatus.minutes)} early`
                      : `Starting ${formatMinutes(timingStatus.minutes)} late`
                    }
                  </p>
                  <p className={`text-xs mt-1 ${
                    timingStatus.type === 'early' ? 'text-yellow-300' : 'text-orange-300'
                  }`}>
                    {timingStatus.type === 'early'
                      ? 'This is fine! Just noting the early start.'
                      : 'Consider notifying the customer if significantly delayed.'
                    }
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Actual Start Date & Time */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Actual Start Date <span className="text-red-400">*</span>
              </label>
              <input
                type="date"
                value={formData.actualStartDate}
                onChange={(e) => handleChange('actualStartDate', e.target.value)}
                max={companyDateKey()}
                className={`w-full px-4 py-2 bg-white/5 border rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 [color-scheme:dark] ${
                  errors.actualStartDate ? 'border-red-400' : 'border-white/10'
                }`}
              />
              {errors.actualStartDate && (
                <p className="mt-1 text-sm text-red-600">{errors.actualStartDate}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Actual Start Time <span className="text-red-400">*</span>
              </label>
              <input
                type="time"
                value={formData.actualStartTime}
                onChange={(e) => handleChange('actualStartTime', e.target.value)}
                className={`w-full px-4 py-2 bg-white/5 border rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 [color-scheme:dark] ${
                  errors.actualStartTime ? 'border-red-400' : 'border-white/10'
                }`}
              />
              {errors.actualStartTime && (
                <p className="mt-1 text-sm text-red-400">{errors.actualStartTime}</p>
              )}
            </div>
          </div>

          {/* Additional Notes */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Start Notes (Optional)
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Any notes about starting the job (site conditions, customer requests, etc.)..."
              rows={3}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
            />
          </div>



    </GlassModal>
  );
};

export default StartJobModal;

